import { rootApi } from "../../../lib/api/rootApi";
import type { AttendanceStatus, ClassWeekCacheKey } from "../types/kioskApiTypes";

type KioskBuilder = Parameters<Parameters<typeof rootApi.injectEndpoints>[0]["endpoints"]>[0];

/**
 * Translate the kiosk's internal `absent_unexcused` status into the
 * canonical `absent_unclear` value used by every other write path in
 * the system at the moment we send to the RPC.
 *
 * Why: the kiosk's 4-button UI labels the "U" button as "ungeklärt"
 * but the underlying `AttendanceStatus` type still uses the legacy
 * `absent_unexcused` enum value. The DB's `attendance_status` enum
 * still accepts both, but the rest of Flexwise has migrated to
 * `absent_unclear` as the canonical "pre-Klärung" state — checked
 * live on prod 2026-05-20: 26,902 absent_unclear rows vs 0
 * absent_unexcused rows in student_attendance_logs.
 *
 * Keeping the kiosk's internal type as `absent_unexcused` avoids a
 * 9-file UI rename; the read-side normalizers in classWeekHelpers /
 * CellAttendance already flatten incoming `absent_unclear` back to
 * the kiosk's `absent_unexcused` for display, so the round-trip
 * stays consistent.
 */
type RpcAttendanceRow = {
  student_id: string;
  status: string;
  note?: string | null;
  notes?: string | null;
  late_minutes?: number | null;
};
function translateAttendanceForWrite<T extends RpcAttendanceRow>(rows: T[]): T[] {
  return rows.map((r) => (
    r.status === "absent_unexcused" ? { ...r, status: "absent_unclear" } : r
  ));
}

export function buildKioskMutations(builder: KioskBuilder) {
  return {
    // 2c. Diary-only save (inline cell edits — no attendance touched).
    //
    // NB: `userId` is required because on a paired Klassenbuch Kiosk
    // the JWT's auth.uid() resolves to the kiosk_id (the device),
    // not the teacher who actually authored the entry. We pass the
    // teacher's user_id explicitly (from the chip-login response)
    // so `created_by` / `updated_by` get the right human. The
    // server-side helper internal.assert_kiosk_or_mfa enforces that
    // userId actually belongs to the kiosk's school. Authenticated
    // browser-tab callers still pass their own auth uid here — same
    // value the RPC would have derived itself, so behaviour for the
    // non-kiosk path is unchanged.
    kioskSaveDiary: builder.mutation<
      { ok: true },
      { lessonId: string; userId: string; entryText: string; homework: string | null; _classWeekKey?: ClassWeekCacheKey }
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        const { error } = await baseQuery({
          type: "rpc",
          rpc: "fn_klassenbuch_upsert_diary_entry",
          params: {
            p_lesson_id:  args.lessonId,
            p_entry_text: args.entryText,
            p_homework:   args.homework,
            p_user_id:    args.userId,
          },
        });
        if (error) return { error };
        return { data: { ok: true } };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        if (!args._classWeekKey) return;
        const key = args._classWeekKey;
        try {
          await queryFulfilled;
          const { klassenbuchKioskApi: api } = await import("./klassenbuchKioskApi");
          dispatch(
            api.util.updateQueryData("kioskGetClassWeek", key, (draft) => {
              // draft is now { cells, calendar_exceptions } — patch on draft.cells.
              const cell = draft.cells.find((c) => c.lesson_id === args.lessonId);
              if (!cell) return;
              cell.diary_text = args.entryText;
              cell.diary_homework = args.homework;
              cell.is_filled = args.entryText.trim().length > 0;
            }),
          );
        } catch {
          // mutation failed — no cache update
        }
      },
    }),

    // 2d. Attendance-only save (inline per-cell anwesenheit).
    // NB: save_lesson_attendance_bulk reads `note` (singular) and
    //     `late_minutes` from each JSONB element.
    kioskSaveAttendance: builder.mutation<
      { ok: true },
      {
        lessonId: string;
        schoolId: string;
        userId: string;
        attendance: {
          student_id: string;
          status: AttendanceStatus;
          note?: string | null;
          late_minutes?: number | null;
        }[];
        _classWeekKey?: ClassWeekCacheKey;
      }
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        const { error } = await baseQuery({
          type: "rpc",
          rpc: "save_lesson_attendance_bulk",
          params: {
            p_lesson_id:         args.lessonId,
            p_school_id:         args.schoolId,
            p_attendance:        translateAttendanceForWrite(args.attendance),
            p_recorded_by:       args.userId,
            p_diary_entry_text:  null,
            p_diary_is_private:  false,
          },
        });
        if (error) return { error };
        return { data: { ok: true } };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        if (!args._classWeekKey) return;
        const key = args._classWeekKey;
        try {
          await queryFulfilled;
          const { klassenbuchKioskApi: api } = await import("./klassenbuchKioskApi");
          dispatch(
            api.util.updateQueryData("kioskGetClassWeek", key, (draft) => {
              const cell = draft.cells.find((c) => c.lesson_id === args.lessonId);
              if (!cell) return;
              cell.attendance_recorded = true;
              cell.absentees = args.attendance.map((a) => ({
                student_id: a.student_id,
                name: "",
                status: a.status,
                notes: a.note ?? null,
                late_minutes: a.late_minutes ?? null,
              }));
            }),
          );
        } catch {
          // mutation failed — no cache update
        }
      },
    }),

    // 2e. Day-note upsert (per-class per-date Bemerkungen).
    //
    // NB: `userId` is required for the same reason as the diary save —
    // on a paired kiosk the JWT's auth.uid() returns kiosk_id (the
    // device, not in user_profiles), which would violate the
    // klassenbuch_day_notes_created_by_fkey constraint. Passing the
    // teacher's user_id explicitly stamps the right `created_by`.
    // See 20260520_kiosk_day_note_paired_callable.sql.
    kioskSaveDayNote: builder.mutation<
      { ok: true },
      { classId: string; userId: string; date: string; text: string; _classWeekKey?: ClassWeekCacheKey }
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        const { error } = await baseQuery({
          type: "rpc",
          rpc: "fn_klassenbuch_upsert_day_note",
          params: {
            p_class_id: args.classId,
            p_date:     args.date,
            p_text:     args.text,
            p_user_id:  args.userId,
          },
        });
        if (error) return { error };
        return { data: { ok: true } };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        if (!args._classWeekKey) return;
        const key = args._classWeekKey;
        try {
          await queryFulfilled;
          const { klassenbuchKioskApi: api } = await import("./klassenbuchKioskApi");
          dispatch(
            api.util.updateQueryData("kioskGetClassWeek", key, (draft) => {
              for (const cell of draft.cells) {
                if (cell.start_at.slice(0, 10) === args.date) {
                  cell.day_note = args.text;
                }
              }
            }),
          );
        } catch {
          // mutation failed — no cache update
        }
      },
    }),

    // 3. Save: diary upsert + attendance bulk in sequence.
    kioskSaveLesson: builder.mutation<
      { ok: true },
      {
        lessonId: string;
        schoolId: string;
        userId: string;
        entryText: string;
        homework: string | null;
        attendance: { student_id: string; status: AttendanceStatus; notes?: string | null }[];
      }
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        // Diary first — the attendance bulk RPC also takes a diary text
        // param, but we use the explicit upsert so we can also persist the
        // homework field (which the bulk RPC doesn't accept).
        const diary = await baseQuery({
          type: "rpc",
          rpc: "fn_klassenbuch_upsert_diary_entry",
          params: {
            p_lesson_id: args.lessonId,
            p_entry_text: args.entryText,
            p_homework: args.homework,
            p_user_id: args.userId,
          },
        });
        if (diary.error) return { error: diary.error };

        const att = await baseQuery({
          type: "rpc",
          rpc: "save_lesson_attendance_bulk",
          params: {
            p_lesson_id: args.lessonId,
            p_school_id: args.schoolId,
            p_attendance: translateAttendanceForWrite(args.attendance),
            p_recorded_by: args.userId,
            p_diary_entry_text: null,
            p_diary_is_private: false,
          },
        });
        if (att.error) return { error: att.error };

        return { data: { ok: true } };
      },
    }),
  };
}
